import React, { useCallback } from 'react';
import { Checkbox, Switch, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { useOrganization } from '@components/OrganizationBoundary';
import type { AwStateStageMember } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import StageMember from '@components/Modals/ApprovalModal/ApprovalModal/components/StageMember';
import { DeleteIcon, InfoIcon } from '@icons';
import './StageMembers.less';

type StageMembersProps = {
  ownerEmail: string | null;
  members: AwStateStageMember[];
  onUpdateMembers: (members: AwStateStageMember[]) => void;
};

export default function StageMembers({
  ownerEmail,
  members,
  onUpdateMembers
}: StageMembersProps) {
  const { currentOrganization } = useOrganization(false);

  const onMemberUpdate = useCallback(
    (member: AwStateStageMember) => {
      onUpdateMembers(
        members.map((x) => {
          if (x.email === member.email) return member;
          return x;
        })
      );
    },
    [members, onUpdateMembers]
  );

  const columns: ColumnsType<AwStateStageMember> = [
    {
      title: (
        <div className="stage-members_head">
          Reviewers
          <div className="stage-members_count">{members.length}</div>
        </div>
      ),
      dataIndex: 'user',
      key: 'user',
      render: (_, record) => {
        return (
          <StageMember member={record} isOwner={record.email === ownerEmail} />
        );
      }
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      render: (_, record) => (
        <Checkbox
          className="create-approval_checkbox"
          checked={record?.canComment}
          onChange={() =>
            onMemberUpdate({
              ...record,
              canComment: !record.canComment
            })
          }
        />
      )
    },
    {
      title: 'Approve',
      dataIndex: 'approve',
      key: 'approve',
      render: (_, record) => {
        return (
          <Checkbox
            className="create-approval_checkbox"
            checked={record.canSubmitDecision}
            onChange={() => {
              const canSubmitDecision = !record.canSubmitDecision;
              onMemberUpdate({
                ...record,
                canSubmitDecision
              });
            }}
          />
        );
      }
    },
    {
      title: (
        <Tooltip
          overlayClassName="stage-card_tooltip"
          title="Users outside the workspace cannot upload media"
          trigger="hover"
          placement="bottom"
        >
          <div className="stage-members_head">
            <span>Upload</span>
            <InfoIcon />
          </div>
        </Tooltip>
      ),
      dataIndex: 'upload',
      key: 'upload',
      render: (_, record) => {
        const disableReason =
          !record.canUploadNewVersion && !record.organizationRole
            ? 'Users outside the workspace cannot upload media'
            : null;
        return (
          <Tooltip
            overlayClassName="stage-card_tooltip"
            title={disableReason}
            trigger="hover"
            placement="top"
          >
            <Checkbox
              className="create-approval_checkbox"
              checked={record.canUploadNewVersion}
              disabled={!!disableReason}
              onChange={() =>
                onMemberUpdate({
                  ...record,
                  canUploadNewVersion: !record.canUploadNewVersion
                })
              }
            />
          </Tooltip>
        );
      }
    },
    {
      title: (
        <Tooltip
          overlayClassName="stage-card_tooltip"
          title="Users outside the workspace cannot manage approval routing"
          trigger="hover"
          placement="bottom"
        >
          <div className="stage-members_head">
            <span>Manage</span>
            <InfoIcon />
          </div>
        </Tooltip>
      ),
      dataIndex: 'manage',
      key: 'manage',
      render: (_, record) => {
        const disableReason = record.me
          ? 'You cannot change your own permissions'
          : !record.canManageAW && !record.organizationRole
          ? 'Users outside the workspace cannot manage approval routing'
          : null;
        return (
          <Tooltip
            overlayClassName="stage-card_tooltip"
            title={disableReason}
            trigger="hover"
            placement="top"
          >
            <Checkbox
              className="create-approval_checkbox"
              disabled={
                !!record.me || (!record.canManageAW && !record.organizationRole)
              }
              checked={record.canManageAW}
              onChange={() =>
                onMemberUpdate({
                  ...record,
                  canManageAW: !record.canManageAW
                })
              }
            />
          </Tooltip>
        );
      }
    },
    {
      title: (
        <Tooltip
          overlayClassName="stage-card_tooltip"
          title={
            <div style={{ textAlign: 'left' }}>
              The reviewer’s comments will be designated as high priority <br />{' '}
              Note: only reviewers with “manage” permissions will see high
              priority comments
            </div>
          }
          trigger="hover"
          placement="bottomRight"
        >
          <div className="stage-members_head">
            <span>Priority</span>
            <InfoIcon />
          </div>
        </Tooltip>
      ),
      dataIndex: 'priority',
      key: 'priority',
      render: (_, record) => (
        <UpgradePlanTooltip
          text=" to a Enterprise plan to access priority comments."
          isCanVisible={!currentOrganization?.entity?.features.priorityComments}
        >
          <Switch
            disabled={!currentOrganization?.entity?.features.priorityComments}
            checked={record.hasCommentPriority}
            onChange={() =>
              onMemberUpdate({
                ...record,
                hasCommentPriority: !record.hasCommentPriority
              })
            }
          />
        </UpgradePlanTooltip>
      )
    },
    {
      title: 'Download',
      dataIndex: 'download',
      key: 'download',
      render: (_, record) => (
        <Switch
          checked={record.canDownload}
          onChange={() =>
            onMemberUpdate({
              ...record,
              canDownload: !record.canDownload
            })
          }
        />
      )
    },
    {
      title: '',
      dataIndex: 'delete',
      key: 'delete',
      className: 'stage-members_delete-td',
      render: (_, record) => {
        if (record.me) return null;
        if (record.email === ownerEmail) return null;
        return (
          <div
            className="stage-members_icon-delete"
            onClick={() =>
              onUpdateMembers(members.filter((x) => x.email !== record.email))
            }
          >
            <DeleteIcon />
          </div>
        );
      }
    }
  ];
  return (
    <Table
      rowKey="email"
      className="stage-members"
      tableLayout="fixed"
      columns={columns}
      dataSource={members}
      pagination={false}
    />
  );
}
